<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu
      :user="user"
      :isMenuOpen="isMenuOpen"
      @toggle-menu="toggleMenu"
      @close-menu="closeMenu"
      @logout="logout"
    />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold">Académico / Actividades</h1>
          <p class="text-gray-600 mb-4">Matrícula: {{ studentId }}</p>
          <div class="hidden">
                    <PeriodSelector 
                      v-if="studentId"
                      :studentId="studentId"
                      v-model="selectedPeriod"
                      @period-changed="loadData"
                    />
          </div>
          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>

          <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ error }}
          </div>

          <div v-else-if="noDataAvailable" class="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            Sin información disponible
          </div>

          <div v-else-if="actividades && actividades.length > 0" class="mb-6">
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-lg font-semibold mb-2">Actividades del Periodo:</h2>
              <span class="text-sm text-gray-600">
                {{ actividades.length }} actividad(es) encontrada(s)
              </span>
            </div>
            
            <!-- Vista de tarjetas (móvil y escritorio) -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div 
                v-for="item in sortedActividades" 
                :key="item.idactivity"
                class="bg-white shadow-md rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              >
                <!-- Encabezado de la tarjeta con color según estado -->
                <div 
                  class="p-4 text-white font-semibold"
                  :class="getStatusColor(item.activity_status)"
                >
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <h3 class="text-lg font-bold mb-1">{{ item.activity_name }}</h3>
                      <p class="text-sm opacity-90">{{ item.subject }}</p>
                    </div>
                    <span class="ml-2 px-2 py-1 bg-white bg-opacity-30 rounded text-xs">
                      {{ item.activity_status }}
                    </span>
                  </div>
                </div>

                <!-- Contenido de la tarjeta -->
                <div class="p-4">
                  <div class="mb-3">
                    <!-- Mostrar fecha estática para estados que no son "Disponible" -->
                    <div v-if="item.activity_status !== 'Disponible'">
                      <p class="text-sm text-gray-600 mb-1">Fecha límite:</p>
                      <p class="text-sm font-semibold text-gray-900">
                        {{ formatDate(item.activity_date_limit) }}
                      </p>
                    </div>
                    
                    <!-- Mostrar contador regresivo para estado "Disponible" sin documento -->
                    <div v-else-if="item.activity_status === 'Disponible' && !item.activity_file">
                      <div class="bg-yellow-50 border border-yellow-200 rounded p-2">
                        <p class="text-sm text-gray-700 mb-1 font-medium">Tiempo restante:</p>
                        <div class="flex items-center justify-between">
                          <p class="text-2xl font-bold text-red-600">
                            {{ getCountdown(item.activity_date_limit) }}
                          </p>
                          <svg class="w-6 h-6 text-red-500 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Para actividades disponibles ya entregadas -->
                    <div v-else-if="item.activity_status === 'Disponible' && item.activity_file">
                      <p class="text-sm text-gray-600 mb-1">Fecha límite:</p>
                      <p class="text-sm font-semibold text-gray-900">
                        {{ formatDate(item.activity_date_limit) }}
                      </p>
                    </div>
                  </div>

                  <!-- Sección de carga de archivo (solo para estado "Disponible") -->
                  <div v-if="item.activity_status === 'Disponible'" class="mb-3">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Subir documento (PDF, máx 2MB)
                    </label>
                    <input 
                      type="file" 
                      accept=".pdf"
                      @change="handleFileChange($event, item)"
                      class="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100
                        cursor-pointer"
                    />
                    <button
                      v-if="item.selectedFile"
                      @click="uploadFile(item)"
                      :disabled="item.uploading"
                      class="mt-2 w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      <span v-if="item.uploading">Subiendo...</span>
                      <span v-else>Enviar documento</span>
                    </button>
                  </div>

                  <!-- Archivo entregado -->
                  <div v-if="item.activity_file && item.activity_status === 'Entregado'" class="mb-3">
                    <p class="text-sm text-gray-600 mb-1">Documento entregado:</p>
                    <a 
                      :href="item.activity_file" 
                      target="_blank"
                      class="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Ver documento
                    </a>
                  </div>

                  <!-- Información de revisión -->
                  <div v-if="item.activity_status === 'Revisada'" class="border-t pt-3 mt-3">
                    <div v-if="item.activity_file" class="mb-3">
                      <p class="text-sm text-gray-600 mb-1">Documento entregado:</p>
                      <a 
                        :href="item.activity_file" 
                        target="_blank"
                        class="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        Ver documento
                      </a>
                    </div>
                    
                    <div v-if="item.qualification" class="mb-2">
                      <p class="text-sm text-gray-600">Calificación:</p>
                      <p class="text-2xl font-bold text-green-600">{{ item.qualification }}</p>
                    </div>
                    
                    <div v-if="item.comments" class="mb-2">
                      <p class="text-sm text-gray-600 mb-1">Comentarios del docente:</p>
                      <p class="text-sm text-gray-800 bg-gray-50 p-2 rounded">{{ item.comments }}</p>
                    </div>
                  </div>

                  <!-- Mensaje informativo según estado -->
                  <div v-if="item.activity_status === 'Próximamente'" class="text-sm text-gray-600 italic">
                    La fecha de captura aún no está disponible
                  </div>
                  <div v-if="item.activity_status === 'Vencida'" class="text-sm text-red-600 font-semibold">
                    Esta actividad ha vencido
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import PeriodSelector from '@/components/PeriodSelector.vue';
import { getActivities, uploadActivity } from "@/services/api";

export default {
  components: {
    SidebarMenu,
    PeriodSelector
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      actividades: null,
      loading: false,
      error: null,
      studentId: null,
      noDataAvailable: false,
      selectedPeriod: '',
      countdownInterval: null,
      currentTimeCDMX: null
    };
  },
  computed: {
    sortedActividades() {
      if (!this.actividades) return [];
      
      return [...this.actividades].sort((a, b) => {
        const dateA = new Date(a.activity_date_limit);
        const dateB = new Date(b.activity_date_limit);
        return dateA - dateB;
      });
    }
  },
  async created() {
    await this.getUserFromBackend();
    this.startCountdownUpdater();
  },
  beforeUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  },
  methods: {
    getCDMXTime() {
      const fechaCDMXString = new Date().toLocaleString('en-US', { timeZone: 'America/Mexico_City' });
      return new Date(fechaCDMXString);
    },

    startCountdownUpdater() {
      this.countdownInterval = setInterval(() => {
        this.currentTimeCDMX = this.getCDMXTime();
        this.checkExpiredActivities();
      }, 1000);
    },

    checkExpiredActivities() {
      if (!this.actividades || !this.currentTimeCDMX) return;
      
      this.actividades.forEach(activity => {
        if (activity.activity_status === 'Disponible' && !activity.activity_file) {
          const timeLimit = new Date(activity.activity_date_limit).getTime();
          const diff = timeLimit - this.currentTimeCDMX.getTime();
          
          if (diff <= 0) {
            this.loadData(this.selectedPeriod);
          }
        }
      });
    },

    getCountdown(dateLimit) {
      // Si currentTimeCDMX no está disponible aún, obtener la hora actual
      const fechaCDMX = this.currentTimeCDMX || this.getCDMXTime();

      const timeLimit = new Date(dateLimit).getTime();
      const diff = timeLimit - fechaCDMX.getTime();

      if (diff <= 0) {
        return 'Vencida';
      }
      
      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / (60 * 60 * 24));
      const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
      const seconds = totalSeconds % 60;
      
      if (days > 0) {
        return `${days} día${days > 1 ? 's' : ''} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      } else {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    getStatusColor(status) {
      const colors = {
        'Próximamente': 'bg-gray-500',
        'Disponible': 'bg-green-600',
        'Vencida': 'bg-red-600',
        'Entregado': 'bg-blue-600',
        'Revisada': 'bg-purple-600'
      };
      return colors[status] || 'bg-gray-500';
    },

    handleFileChange(event, activity) {
      const file = event.target.files[0];
      
      if (!file) return;
      
      // Validar que sea PDF
      if (file.type !== 'application/pdf') {
        this.$alert(
            'Atención',
            'Solo se permiten archivos PDF',
            'warning'
        );

        event.target.value = '';
        return;
      }
      
      // Validar tamaño (2MB = 2097152 bytes)
      if (file.size > 2097152) {
        this.$alert(
            'Atención',
            'El archivo no debe superar los 2MB',
            'warning'
        );
        event.target.value = '';
        return;
      }
      
      // Guardar el archivo en la actividad
      activity.selectedFile = file;
    },

    async uploadFile(activity) {
      if (!activity.selectedFile) return;
      
      const fechaCDMX = this.getCDMXTime();
      const timeLimit = new Date(activity.activity_date_limit).getTime();
      
      if (fechaCDMX.getTime() >= timeLimit) {
        this.$alert(
          'Tiempo vencido',
          'El tiempo límite para entregar esta actividad ha expirado',
          'warning'
        );
        activity.selectedFile = null;
        await this.loadData(this.selectedPeriod);
        return;
      }
      
      try {
        activity.uploading = true;
        
        // Convertir archivo a base64
        const base64 = await this.fileToBase64(activity.selectedFile);
        
        // Preparar datos para enviar
        const params = {
          idactivity: activity.idactivity,
          idstudent: this.studentId,
          idperiod: activity.idperiod,
          code_subject: activity.code_subject,
          base64: base64
        };
        
        // Enviar archivo
        await uploadActivity(params);
        
        this.$alert(
          '¡Excelente!',
          'Documento subido exitosamente',
          'success'
        );
        
        // Recargar datos
        await this.loadData(this.selectedPeriod);
        
      } catch (error) {
        console.error('Error al subir el archivo:', error);
        console.error('Detalles del error:', error.response?.data);
        this.$alert(
            'Error',
            'Error al subir el documento. Por favor, intente nuevamente.',
            'error'
        );
      } finally {
        activity.uploading = false;
        activity.selectedFile = null;
      }
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          // Extraer solo la parte base64 (sin el prefijo data:application/pdf;base64,)
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = error => reject(error);
      });
    },
    
    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
        
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.studentId = this.user.email.match(/^a(\d+)@/)[1];

          await this.loadData();
        } else {
          this.error = "No se pudo extraer el ID de estudiante del email.";
          this.loading = false;
        }
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },

    async loadData(periodId) {
      if (!periodId) return;
      
      try {
        this.loading = true;
        this.error = null;
        this.noDataAvailable = false;

        const response = await getActivities({ 
          idstudent: this.studentId, 
          //idperiod: periodId 
        });
        
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          this.actividades = response.data.map(act => ({
            ...act,
            selectedFile: null,
            uploading: false
          }));
        } else {
          this.actividades = [];
          this.noDataAvailable = true;
        }
        
        
      } catch (error) {
        this.loading = false;
        
        if (error.response && error.response.status === 404) {
          this.noDataAvailable = true;
          return;
        }
        
        console.error("Error al obtener los datos:", error);
        this.error = "Error al cargar los datos. Por favor, intente nuevamente.";
      } finally {
        this.loading = false;
      }
    },

    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },

    closeMenu() {
      this.isMenuOpen = false;
    },

    async logout() {
      try {
        await axios.get('/sanctum/csrf-cookie');
        await new Promise(resolve => setTimeout(resolve, 100));

        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        console.log(response);
        
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>